import React from 'react';
import { Button } from './Button';
import { motion } from 'framer-motion';
import { CheckCircle2, Users, Clock, TrendingUp, Target } from 'lucide-react';
interface HeroProps {
  onApplyClick: () => void;
}
export function Hero({
  onApplyClick
}: HeroProps) {
  return <section className="relative max-w-7xl mx-auto px-6 py-20 lg:py-32 overflow-hidden" data-id="element-224">
      {/* Background Gradients */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-mint-500/10 via-wave-500/5 to-transparent rounded-[100%] blur-3xl -z-10" data-id="element-225" />

      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-226">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-227">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-mint-500/10 border border-mint-500/20 text-mint-500 text-xs font-bold uppercase tracking-wider mb-6" data-id="element-228">
            <span className="relative flex h-2 w-2" data-id="element-229">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-mint-400 opacity-75" data-id="element-230"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-mint-500" data-id="element-231"></span>
            </span>
            Accepting Applications
          </div>

          <h1 className="text-5xl lg:text-7xl font-bold leading-tight mb-6 tracking-tight" data-id="element-232">
            Turn fans into{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-mint-500 to-wave-500" data-id="element-233">
              builders
            </span>
            .
          </h1>

          <p className="text-lg text-slate-300 mb-8 leading-relaxed max-w-lg" data-id="element-234">
            Foster a core of builders who show up for you with their{' '}
            <span className="text-mint-500 font-semibold" data-id="element-235">
              time, attention, and money,
            </span>{' '}
            and use their voice to help you thrive.
          </p>

          <div className="mb-8" data-id="element-236">
            <Button variant="primary" onClick={onApplyClick} className="bg-gradient-to-r from-mint-500 to-wave-500 border-none text-navy-950 font-bold hover:shadow-lg hover:shadow-mint-500/25 transition-all duration-300 text-lg px-8 py-4" data-id="element-237">
              Apply to the Creator Lab
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 text-xs text-slate-400" data-id="element-238">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-navy-900/50 border border-slate-800" data-id="element-239">
              <CheckCircle2 className="w-3 h-3 text-mint-500" data-id="element-240" />
              <span data-id="element-241">4 weeks</span>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-navy-900/50 border border-slate-800" data-id="element-242">
              <CheckCircle2 className="w-3 h-3 text-mint-500" data-id="element-243" />
              <span data-id="element-244">Fully remote</span>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-navy-900/50 border border-slate-800" data-id="element-245">
              <CheckCircle2 className="w-3 h-3 text-mint-500" data-id="element-246" />
              <span data-id="element-247">$2,000 stipend</span>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-navy-900/50 border border-slate-800" data-id="element-248">
              <CheckCircle2 className="w-3 h-3 text-mint-500" data-id="element-249" />
              <span data-id="element-250">5 creators</span>
            </div>
          </div>
        </motion.div>

        {/* Hero Visual - Market Card with 3 Options */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="relative" data-id="element-251">
          {/* Glow effect behind card */}
          <div className="absolute inset-0 bg-gradient-to-r from-mint-500 to-wave-500 blur-2xl opacity-20 rounded-3xl transform rotate-3 scale-105" data-id="element-252" />

          <div className="relative bg-navy-900 rounded-2xl p-1 border border-slate-800 shadow-2xl" data-id="element-253">
            {/* Gradient Border Inner */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-slate-700 to-transparent opacity-50 pointer-events-none" data-id="element-254" />

            <div className="bg-navy-950 rounded-xl p-6 relative overflow-hidden" data-id="element-255">
              {/* Card Header */}
              <div className="flex items-center justify-between mb-6" data-id="element-256">
                <div className="flex items-center gap-3" data-id="element-257">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-mint-500 to-wave-500 p-0.5" data-id="element-258">
                    <div className="w-full h-full bg-navy-900 rounded-full flex items-center justify-center text-xs font-bold text-white" data-id="element-259">
                      CN
                    </div>
                  </div>
                  <div data-id="element-260">
                    <div className="text-sm font-bold text-white" data-id="element-261">
                      @CreatorName
                    </div>
                    <div className="text-xs text-slate-400" data-id="element-262">Market #102</div>
                  </div>
                </div>
                <div className="px-2 py-1 rounded bg-mint-500/10 text-mint-500 text-xs font-mono font-bold animate-pulse" data-id="element-263">
                  LIVE
                </div>
              </div>

              {/* Goal */}
              <div className="flex items-center gap-2 mb-4 p-2.5 rounded-lg bg-gradient-to-r from-mint-500/10 to-wave-500/10 border border-mint-500/20" data-id="element-264">
                <Target className="w-4 h-4 text-mint-500 flex-shrink-0" data-id="element-265" />
                <div data-id="element-266">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider font-bold" data-id="element-267">
                    Goal
                  </div>
                  <div className="text-xs font-bold text-white" data-id="element-268">
                    Hit 100K views in 7 days
                  </div>
                </div>
              </div>

              {/* Question */}
              <h3 className="text-lg font-bold text-white mb-5" data-id="element-269">
                Which format will hit the goal?
              </h3>

              {/* 3 Options (A/B/C) */}
              <div className="space-y-3 mb-6" data-id="element-270">
                {/* Option A - Selected */}
                <div className="relative group cursor-pointer" data-id="element-271">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-mint-500 to-wave-500 rounded-lg opacity-100 blur-[1px]" data-id="element-272" />
                  <div className="relative bg-navy-900 rounded-lg p-3 flex justify-between items-center" data-id="element-273">
                    <div className="flex flex-col" data-id="element-274">
                      <span className="font-semibold text-white text-sm" data-id="element-275">
                        Long-form deep dive
                      </span>
                      <span className="text-[10px] text-mint-500 font-bold mt-0.5" data-id="element-276">
                        YOUR TAKE
                      </span>
                    </div>
                    <span className="text-base font-bold text-mint-500" data-id="element-277">
                      45%
                    </span>
                    <div className="absolute bottom-0 left-0 h-0.5 bg-slate-800 w-full rounded-b-lg overflow-hidden" data-id="element-278">
                      <div className="h-full bg-gradient-to-r from-mint-500 to-wave-500 w-[45%]" data-id="element-279" />
                    </div>
                  </div>
                </div>

                {/* Option B */}
                <div className="relative group cursor-pointer" data-id="element-280">
                  <div className="relative bg-navy-900 border border-slate-800 rounded-lg p-3 flex justify-between items-center hover:border-slate-600 transition-colors" data-id="element-281">
                    <span className="font-medium text-slate-300 text-sm" data-id="element-282">
                      Challenge format
                    </span>
                    <span className="text-base font-bold text-slate-500" data-id="element-283">
                      32%
                    </span>
                    <div className="absolute bottom-0 left-0 h-0.5 bg-slate-800 w-full rounded-b-lg overflow-hidden" data-id="element-284">
                      <div className="h-full bg-slate-600 w-[32%]" data-id="element-285" />
                    </div>
                  </div>
                </div>

                {/* Option C */}
                <div className="relative group cursor-pointer" data-id="element-286">
                  <div className="relative bg-navy-900 border border-slate-800 rounded-lg p-3 flex justify-between items-center hover:border-slate-600 transition-colors" data-id="element-287">
                    <span className="font-medium text-slate-300 text-sm" data-id="element-288">
                      Collab with @Partner
                    </span>
                    <span className="text-base font-bold text-slate-500" data-id="element-289">
                      23%
                    </span>
                    <div className="absolute bottom-0 left-0 h-0.5 bg-slate-800 w-full rounded-b-lg overflow-hidden" data-id="element-290">
                      <div className="h-full bg-slate-600 w-[23%]" data-id="element-291" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Stats */}
              <div className="flex items-center justify-between text-xs text-slate-400 pt-4 border-t border-slate-800/50" data-id="element-292">
                <div className="flex items-center gap-3" data-id="element-293">
                  <div className="flex items-center gap-1" data-id="element-294">
                    <Users className="w-3 h-3" data-id="element-295" />
                    <span data-id="element-296">2.8k Takes</span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-297">
                    <TrendingUp className="w-3 h-3" data-id="element-298" />
                    <span data-id="element-299">125k Pts</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-mint-500" data-id="element-300">
                  <Clock className="w-3 h-3" data-id="element-301" />
                  <span data-id="element-302">2d left</span>
                </div>
              </div>
            </div>
          </div>

          {/* Floating Elements */}
          <motion.div animate={{
          y: [0, -10, 0]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute -right-4 top-20 bg-navy-800 p-3 rounded-xl border border-slate-700 shadow-xl max-w-[140px]" data-id="element-303">
            <div className="flex items-center gap-2 mb-1" data-id="element-304">
              <div className="w-2 h-2 rounded-full bg-mint-500" data-id="element-305" />
              <span className="text-[9px] uppercase font-bold text-slate-400" data-id="element-306">
                Top Sharp
              </span>
            </div>
            <div className="text-xs font-medium text-white" data-id="element-307">
              <span className="text-mint-500 font-bold" data-id="element-308">@SuperFan</span> 98%
              accuracy
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}